/**
 * Publish orchestration — makes intervals.icu match the plan with minimal churn.
 *
 * Earlier this was "create everything, then delete everything that was already
 * there" — every publish wiped the calendar clean and re-pushed the whole plan,
 * even sessions that hadn't changed. That's a lot of needless create/delete
 * traffic and it reshuffles ids on every publish.
 *
 * Now it's a real reconciliation (a diff):
 *   1. FETCH the true current calendar.
 *   2. DIFF it against the plan by workout signature (date + name + sport +
 *      description). Sessions already on the calendar unchanged are left exactly
 *      as they are — not touched at all.
 *   3. CREATE only the sessions that are new (or whose date/content changed —
 *      a "move" or "replace" looks like a new signature).
 *   4. DELETE only the in-window non-race events that no longer match any planned
 *      session (the old copy of something that moved/changed, leftover
 *      duplicates, or sessions dropped from the plan). Races are never touched.
 *
 * Create happens before delete so the calendar is never momentarily emptied, and
 * unchanged sessions stay in place throughout. Re-publishing an unchanged plan is
 * a no-op: nothing is created, nothing is deleted.
 */
import { PlanActionOverrides, overrideKey } from "./planActionOverrides";

export interface PublishEvent {
  id: number;
  start_date_local: string;   // "YYYY-MM-DDTHH:mm:ss"
  category?: string;          // "RACE_A" / "WORKOUT" / …
  name?: string;
  type?: string;              // sport, e.g. "Run" / "Ride"
  description?: string;
}

export interface PublishPayload {
  start_date_local: string;
  name: string;
  type: string;
  description: string;
  category: string;
}

export interface PublishApi {
  createEvents(payloads: PublishPayload[], onProgress?: (done: number, total: number) => void): Promise<number[]>;
  fetchEvents(): Promise<PublishEvent[]>;
  deleteEvent(id: number): Promise<void>;
}

export interface PublishSession {
  date: string;            // "YYYY-MM-DD"
  name: string;
  sport: string;
  description: string;
  noPublish?: boolean;     // counted for volume but not pushed (e.g. commute)
}

export interface PublishProgress {
  done: number;
  total: number;
  phase: "create" | "clean";
}

export interface PublishResult {
  created: number[];       // ids of events we created this publish
  unchanged: number[];     // ids of existing events left in place (already matched the plan)
  removed: number[];       // ids of events we deleted
  events: PublishEvent[];  // calendar after publish, minus the plan's own events
}

export interface PublishDiff {
  toCreate: PublishPayload[];   // planned sessions not already on the calendar
  unchanged: number[];          // ids of existing events that already match the plan
  toDelete: number[];           // ids of in-window non-race events the plan no longer wants
}

const RACE_RE = /^RACE/i;

/**
 * Identity of a workout for matching: same day + name + sport + description means
 * "the same session". A change to any of those (including the date — a move) makes
 * it a different signature, so it shows up as a delete-old + create-new pair.
 * Description whitespace is normalised so trivial formatting differences coming
 * back from intervals.icu don't force a needless re-publish.
 */
function normalizeDescription(description: string): string {
  return description.replace(/\r\n/g, "\n").trim();
}

function payloadSignature(p: PublishPayload): string {
  const date = p.start_date_local.slice(0, 10);
  return [date, p.name, p.type, normalizeDescription(p.description)].join(" ");
}

function eventSignature(e: PublishEvent): string {
  const date = e.start_date_local.slice(0, 10);
  return [date, e.name ?? "", e.type ?? "", normalizeDescription(e.description ?? "")].join(" ");
}

/** Build the create payloads for a plan (skips noPublish sessions). */
export function buildPublishPayloads(sessions: PublishSession[]): PublishPayload[] {
  return sessions
    .filter(s => !s.noPublish)
    .map(s => ({
      start_date_local: `${s.date}T10:00:00`,
      name: s.name,
      type: s.sport,
      description: s.description,
      category: "WORKOUT",
    }));
}

/** The publish window: today → race (starts at today so stray earlier events are cleaned too). */
export function publishRange(sessions: PublishSession[], raceDate: string, today: string): { rangeStart: string; rangeEnd: string } {
  const firstSession = sessions[0]?.date ?? today;
  return { rangeStart: today < firstSession ? today : firstSession, rangeEnd: raceDate };
}

/**
 * Diff the plan against the real calendar. For each planned session, if an
 * unchanged copy already exists in the window we keep it; otherwise we create it.
 * Any in-window non-race event left unmatched (moved/replaced session, duplicate,
 * or a dropped session) is deleted. Races and out-of-window events are ignored.
 */
export function reconcile(
  freshEvents: PublishEvent[],
  payloads: PublishPayload[],
  rangeStart: string,
  rangeEnd: string,
  overrides: PlanActionOverrides = {},
): PublishDiff {
  const inWindow = freshEvents.filter(e => {
    const d = e.start_date_local.slice(0, 10);
    return d >= rangeStart && d <= rangeEnd && !RACE_RE.test(e.category ?? "");
  });

  // Index existing events by signature so each planned session can claim one.
  const bySignature = new Map<string, number[]>();
  for (const e of inWindow) {
    const sig = eventSignature(e);
    const bucket = bySignature.get(sig);
    if (bucket) bucket.push(e.id);
    else bySignature.set(sig, [e.id]);
  }

  const unchanged: number[] = [];
  const toCreate: PublishPayload[] = [];
  for (const p of payloads) {
    const bucket = bySignature.get(payloadSignature(p));
    if (bucket && bucket.length > 0) {
      unchanged.push(bucket.shift()!);   // an existing event already matches — keep it
    } else {
      toCreate.push(p);                  // new, moved, or changed — must create
    }
  }

  const kept = new Set(unchanged);
  const toDelete = inWindow.filter(e => !kept.has(e.id)).map(e => e.id);

  // Apply user "planned action" overrides:
  //   protect → never delete this event;  remove → delete it / don't create it.
  const ovr = (date: string, name: string) => overrides[overrideKey(date, name)];
  const delSet = new Set(toDelete);
  for (const e of inWindow) {
    const o = ovr(e.start_date_local.slice(0, 10), e.name ?? "");
    if (o === "remove") delSet.add(e.id);
    else if (o === "protect") delSet.delete(e.id);
  }
  const toCreate2 = toCreate.filter(p => ovr(p.start_date_local.slice(0, 10), p.name) !== "remove");
  const unchanged2 = unchanged.filter(id => !delSet.has(id));
  return { toCreate: toCreate2, unchanged: unchanged2, toDelete: Array.from(delSet) };
}

export async function publishPlan(
  api: PublishApi,
  sessions: PublishSession[],
  raceDate: string,
  today: string,
  onProgress?: (p: PublishProgress) => void,
  overrides: PlanActionOverrides = {},
): Promise<PublishResult> {
  const payloads = buildPublishPayloads(sessions);
  const { rangeStart, rangeEnd } = publishRange(sessions, raceDate, today);

  // 1) fetch the real calendar and diff the plan against it
  const fresh = await api.fetchEvents();
  const { toCreate, unchanged, toDelete } = reconcile(fresh, payloads, rangeStart, rangeEnd, overrides);

  // 2) create only the new/changed sessions (before deleting, so the calendar is
  //    never momentarily emptied). Unchanged sessions are left untouched.
  let created: number[] = [];
  if (toCreate.length > 0) {
    created = await api.createEvents(toCreate, (done, total) => onProgress?.({ done, total, phase: "create" }));
  }

  // 3) delete the stale leftovers in parallel chunks with progress
  onProgress?.({ done: 0, total: toDelete.length, phase: "clean" });
  let removed = 0;
  const CHUNK = 8;
  for (let j = 0; j < toDelete.length; j += CHUNK) {
    const batch = toDelete.slice(j, j + CHUNK);
    await Promise.all(batch.map(id => api.deleteEvent(id).catch(() => {})));
    removed += batch.length;
    onProgress?.({ done: removed, total: toDelete.length, phase: "clean" });
  }

  // Calendar minus the plan's own events (created + kept) for syncing app state —
  // so the green plan isn't also shown as a faded "existing" duplicate.
  const planOwned = new Set<number>([...created, ...unchanged]);
  const deleteSet = new Set(toDelete);
  const events = fresh.filter(e => !deleteSet.has(e.id) && !planOwned.has(e.id));
  return { created, unchanged, removed: toDelete, events };
}
